/**
 * Permit Wall sound-unlock overlay (DISPLAY-only office TV).
 *
 * Browser autoplay policy blocks the first sound until the document has
 * received a real user gesture. A wall TV has no one clicking, so we show
 * a full-screen "tap to enable" overlay that runs the dashboard live
 * behind it; the first tap (or any key/touch) unlocks audio and dismisses
 * the overlay. This is the same proven mechanism the dashboard uses
 * (`startInteractionTracking` + `isAudioUnlocked` in `lib/sounds`), just
 * presented full-screen instead of as a corner banner.
 *
 * Documented constraint for the setup runbook: no sound plays until this
 * overlay has been tapped once after the page loads. That is a browser
 * guarantee, not a bug.
 */

import { useEffect, useState } from "react";
import { isAudioUnlocked, startInteractionTracking } from "@/lib/sounds";

const POLL_MS = 250;

export function SoundUnlockOverlay() {
  const [unlocked, setUnlocked] = useState(() => isAudioUnlocked());

  useEffect(() => {
    if (unlocked) return;
    startInteractionTracking();
    const id = window.setInterval(() => {
      if (isAudioUnlocked()) {
        setUnlocked(true);
        window.clearInterval(id);
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [unlocked]);

  if (unlocked) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="اضغط لتفعيل الصوت"
      onClick={() => setUnlocked(true)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "3.4vh",
        background: "var(--overlay-bg)",
        backdropFilter: "blur(9px)",
        WebkitBackdropFilter: "blur(9px)",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: "7.5vw",
          height: "7.5vw",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(67,217,138,0.12)",
          border: "0.2vw solid rgba(67,217,138,0.5)",
          animation: "wallSoftPulse 2.1s ease-in-out infinite",
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5fe39a"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: "3.4vw", height: "3.4vw" }}
          aria-hidden
        >
          <path d="M11 5L6 9H2v6h4l5 4V5z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </div>
      <div style={{ fontSize: "2.6vw", fontWeight: 800, color: "var(--text)", letterSpacing: "-0.01em" }}>
        اضغط لتفعيل الصوت
      </div>
      <div
        style={{
          fontSize: "1.25vw",
          fontWeight: 600,
          color: "var(--text-dim)",
          textAlign: "center",
          lineHeight: 1.6,
        }}
      >
        اضغط في أي مكان أو على أي زر للبدء
        <br />
        اللوحة تعمل الآن خلف هذه الرسالة
      </div>
    </div>
  );
}
